import { faBuilding, faMapMarkerAlt, faPhone, faEnvelope, faUser, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '../../context/AppContext';
import { EditCompanyForm } from '../forms';
import React from 'react';
export const CompanyDetails = ({ company, showEditButton = false }) => {
    const { user } = useAppContext();

    if (!company) {
        return (
            <div className="company-container">
                <div className="no-data-state">
                    <h2>No Company Data</h2>
                    <p>Unable to load company information.</p>
                </div>
            </div>
        );
    }

    const formatAddress = (address) => {
        if (!address) return 'Not provided';
        
        const parts = [
            address.address,
            address.city,
            address.state,
            address.zip
        ].filter(Boolean);
        
        return parts.length > 0 ? parts.join(', ') : 'Not provided';
    };

    const formatContactName = (firstName, lastName) => {
        if (!firstName && !lastName) return 'Not provided';
        return [firstName, lastName].filter(Boolean).join(' ') || 'Not provided';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not available';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className="company-container">
            <div className="company-card">
                <div className="company-card-header">
                    <div className="company-title">
                        <FontAwesomeIcon icon={faBuilding} className="company-icon" />
                        <h1>{company.company_name || 'Company Name Not Set'}</h1>
                    </div>
                    {showEditButton && (user?.is_admin || user?.is_super_admin) && (
                        <EditCompanyForm company={company} />
                    )}
                </div>

                <div className="company-content">
                    {/* Company Details Section */}
                    <div className="company-section">
                        <h3>Company Details</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Company Type</label>
                                <span>{company.company_type || 'Not specified'}</span>
                            </div>
                            {company.company_type !== 'Property Owner' && (
                                <div className="info-item">
                                    <label>Discount Rate</label>
                                    <span>
                                        {company.discount_rate ? `${company.discount_rate}%` : 'Not set'}
                                    </span>
                                </div>
                            )}
                            {user?.user_type === 'Internal' && (
                                <>
                                    <div className="info-item">
                                        <label>Sales Tax ID</label>
                                        <span>{company.sales_tax_id || 'Not provided'}</span>
                                    </div>
                                    <div className="info-item">
                                        <label>Tax Forms Received</label>
                                        <span className="status-indicator">
                                            {company.sales_tax_forms_received ? (
                                                <FontAwesomeIcon icon={faCheckCircle} className="status-success" />
                                            ) : (
                                                <FontAwesomeIcon icon={faTimesCircle} className="status-error" />
                                            )}
                                            {company.sales_tax_forms_received ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Primary Address Section */}
                    <div className="company-section">
                        <h3>Primary Address</h3>
                        <div className="address-info">
                            <div className="info-item">
                                <label>Street Address</label>
                                <span>
                                    {company.street_address_1 || 'Not provided'}
                                    {company.street_address_2 && (
                                        <div>
                                            <br />
                                            {company.street_address_2}
                                        </div>
                                    )}
                                </span>
                            </div>
                            <div className="address-row">
                                <div className="info-item">
                                    <label>City</label>
                                    <span>{company.city || 'Not provided'}</span>
                                </div>
                                <div className="info-item">
                                    <label>State</label>
                                    <span>{company.state || 'Not provided'}</span>
                                </div>
                                <div className="info-item">
                                    <label>ZIP Code</label>
                                    <span>{company.zip_code || 'Not provided'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Primary Contact Section */}
                    <div className="company-section">
                        <h3>Primary Contact</h3>
                        <div className="contact-info">
                            <div className="info-item">
                                <label>Contact Name</label>
                                <span>
                                    <FontAwesomeIcon icon={faUser} className="contact-icon" />
                                    {formatContactName(company.primary_contact_first_name, company.primary_contact_last_name)}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Email</label>
                                <span>
                                    <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                                    {company.primary_contact_email || 'Not provided'}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Phone</label>
                                <span>
                                    <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                                    {company.primary_contact_phone || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Billing Address Section */}
                    {company.billing_address && (
                        <div className="company-section">
                            <h3>Billing Address</h3>
                            <div className="address-info">
                                <div className="info-item">
                                    <label>Billing Contact</label>
                                    <span>
                                        <FontAwesomeIcon icon={faUser} className="contact-icon" />
                                        {formatContactName(company.billing_address.first, company.billing_address.last)}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Address</label>
                                    <span>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
                                        {formatAddress(company.billing_address)}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Phone</label>
                                    <span>
                                        <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                                        {company.billing_address.phone || 'Not provided'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Email</label>
                                    <span>
                                        <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                                        {company.billing_address.email || 'Not provided'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Shipping Address Section */}
                    {company.shipping_address && (
                        <div className="company-section">
                            <h3>Shipping Address</h3>
                            <div className="address-info">
                                <div className="info-item">
                                    <label>Shipping Contact</label>
                                    <span>
                                        <FontAwesomeIcon icon={faUser} className="contact-icon" />
                                        {formatContactName(company.shipping_address.first, company.shipping_address.last)}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Address</label>
                                    <span>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
                                        {formatAddress(company.shipping_address)}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Phone</label>
                                    <span>
                                        <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                                        {company.shipping_address.phone || 'Not provided'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Email</label>
                                    <span>
                                        <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                                        {company.shipping_address.email || 'Not provided'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* System Information */}
                    <div className="company-section">
                        <h3>System Information</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Created</label>
                                <span>{formatDate(company.created_at)}</span>
                            </div>
                            <div className="info-item">
                                <label>Last Updated</label>
                                <span>{formatDate(company.updated_at)}</span>
                            </div>
                            <div className="info-item">
                                <label>Status</label>
                                <span className="status-indicator">
                                    {company.is_archived ? (
                                        <>
                                            <FontAwesomeIcon icon={faTimesCircle} className="status-error" />
                                            Archived
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCheckCircle} className="status-success" />
                                            Active
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

